import { motion } from "framer-motion";
import { ArrowRight, PenLine, ClipboardCheck, Edit3, Building2 } from "lucide-react";

const AUDIENCES = [
  {
    id: "authors",
    icon: PenLine,
    title: "Authors",
    benefits: [
      "Open access & global indexation",
      "Median 28-day first decision",
      "Editorial guidance throughout review",
    ],
    links: [
      { label: "Submission Guidelines", href: "#contact" },
      { label: "Author Charter", href: "#contact" },
    ],
  },
  {
    id: "reviewers",
    icon: ClipboardCheck,
    title: "Reviewers",
    benefits: [
      "Recognized via Publons / ORCID",
      "Structured reviewer training",
      "Diverse international panels",
    ],
    links: [
      { label: "Join Reviewer Pool", href: "#contact" },
      { label: "Review Guidelines", href: "#contact" },
    ],
  },
  {
    id: "editors",
    icon: Edit3,
    title: "Editors",
    benefits: [
      "Editorial independence guaranteed",
      "Subject-specialty board appointments",
      "Editorial development workshops",
    ],
    links: [
      { label: "Editorial Board", href: "#contact" },
      { label: "Editor Resources", href: "#contact" },
    ],
  },
  {
    id: "institutions",
    icon: Building2,
    title: "Institutions",
    benefits: [
      "Institutional publishing agreements",
      "Open access reporting & metrics",
      "Research dissemination support",
    ],
    links: [
      { label: "Partnership Inquiries", href: "#contact" },
      { label: "Institutional Discounts", href: "#contact" },
    ],
  },
];

export default function ForAuthors() {
  return (
    <section
      id="authors"
      data-testid="for-authors-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 05 / For the Research Community
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02] max-w-3xl">
              Supporting researchers <em className="not-italic text-[var(--sci-teal)]">worldwide</em>.
            </h2>
          </div>
          <p className="md:max-w-sm text-[var(--sci-text-muted)] leading-relaxed">
            Whether you publish, review, edit, or collaborate institutionally —
            we provide the editorial infrastructure, recognition, and reach.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: 0.06 * i }}
                data-testid={`audience-card-${a.id}`}
                className="group relative bg-white border border-[var(--sci-border)] card-lift p-8 md:p-10 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-px h-0 bg-[var(--sci-teal)] group-hover:h-full transition-all duration-500" />
                <div className="flex items-start justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-[var(--sci-navy)]/15 flex items-center justify-center text-[var(--sci-teal)] group-hover:bg-[var(--sci-teal)] group-hover:text-white group-hover:border-[var(--sci-teal)] transition-all">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="overline text-[var(--sci-text-muted)]">
                        For
                      </div>
                      <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-none mt-1">
                        {a.title}
                      </h3>
                    </div>
                  </div>
                  <div className="font-mono-sci text-xs text-[var(--sci-text-muted)]">
                    0{i + 1}
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {a.benefits.map((b, k) => (
                    <li
                      key={k}
                      className="flex items-start gap-3 text-sm text-[var(--sci-text)]/85"
                    >
                      <span className="mt-1.5 w-2 h-px bg-[var(--sci-teal)] shrink-0" />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-6 border-t border-[var(--sci-border)] flex flex-wrap gap-x-6 gap-y-2">
                  {a.links.map((l) => (
                    <a
                      key={l.label}
                      href={l.href}
                      data-testid={`audience-link-${a.id}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group/link inline-flex items-center gap-2 text-sm text-[var(--sci-navy)] hover:text-[var(--sci-teal)] transition-colors"
                    >
                      {l.label}
                      <ArrowRight
                        size={14}
                        className="transition-transform group-hover/link:translate-x-1"
                      />
                    </a>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
